import { byPrefixAndName } from '@awesome.me/kit-f16c5d7bca/icons'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams, Link as NavLink } from 'react-router-dom'
import { resetPassword, verifyPasswordToken } from '../../redux/storeConfig/slices/slice.auth/auth.thunks'
import { validators } from '../../utility/Utils'

function ResetPasswordForm() {
  /** @type {import('../../redux/storeConfig/slices/slice.auth/auth.types').AuthSlice} */
  const { status, task, errors } = useSelector(state => state.auth)
  const dispatch = useDispatch()

  const { register, handleSubmit, formState, getValues } = useForm({ mode: 'onSubmit' })

  const history = useHistory()
  const { token } = useParams()

  const [showPassword, setShowPassword] = useState({ newPassword: false, confirmPassword: false })

  useEffect(() => {
    dispatch(verifyPasswordToken(token))
  }, [dispatch, token])

  useEffect(() => {
    if (status === 'succeeded' && task === 'resetPassword')
      history.push({ pathname: `/reset_password/${token}`, search: '?reset=true' })
  }, [history, status, task, token])

  const onSubmit = useCallback(
    async data => {
      const { newPassword } = data

      dispatch(resetPassword(token, newPassword))
    },
    [token, dispatch]
  )

  if (status === 'failed' && task === 'verifyPasswordToken')
    return (
      <Stack spacing="4">
        <Alert status="error">
          <AlertIcon />
          <AlertDescription>
            {errors.find(e => e.action === 'verify_token')?.message ?? 'An error occurred while verifying your token.'}
          </AlertDescription>
        </Alert>
        <Link as={NavLink} to="/forgot_password">
          Try again
        </Link>
      </Stack>
    )

  if (status === 'succeeded' && task === 'resetPassword')
    return (
      <Stack spacing="4">
        <Alert status="success">
          <AlertIcon />
          <AlertDescription>Your password has been successfully updated.</AlertDescription>
        </Alert>
        <Link as={NavLink} to="/login">
          Return to Sign In
        </Link>
      </Stack>
    )

  return (
    <Stack as="form" spacing="4" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Alert hidden={task !== 'resetPassword' || status !== 'failed' || !errors.length} status="error">
        <AlertIcon />
        <AlertDescription>{errors[0]?.message}</AlertDescription>
      </Alert>
      <FormControl isRequired isInvalid={formState.errors.newPassword}>
        <InputGroup autoFocus>
          <Input
            type={showPassword.newPassword ? 'text' : 'password'}
            placeholder="Enter your new password"
            {...register('newPassword', { validate: validators.password })}
          />
          <InputRightElement
            cursor="pointer"
            onClick={() => setShowPassword(prev => ({ ...prev, newPassword: !prev.newPassword }))}
          >
            <FontAwesomeIcon size="sm" icon={byPrefixAndName['fad'][showPassword.newPassword ? 'eye-slash' : 'eye']} />
          </InputRightElement>
        </InputGroup>
        <FormErrorMessage>{formState.errors?.newPassword?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isRequired isInvalid={formState.errors.confirmPassword}>
        <InputGroup>
          <Input
            type={showPassword.confirmPassword ? 'text' : 'password'}
            placeholder="Confirm new password"
            {...register('confirmPassword', {
              validate: v => {
                const { newPassword } = getValues()

                if (v !== newPassword) return 'Passwords must match'

                return validators.password(v)
              }
            })}
          />
          <InputRightElement
            cursor="pointer"
            onClick={() => setShowPassword(prev => ({ ...prev, confirmPassword: !prev.confirmPassword }))}
          >
            <FontAwesomeIcon
              size="sm"
              icon={byPrefixAndName['fad'][showPassword.confirmPassword ? 'eye-slash' : 'eye']}
            />
          </InputRightElement>
        </InputGroup>
        <FormErrorMessage>{formState.errors?.confirmPassword?.message}</FormErrorMessage>
      </FormControl>
      <Button type="submit" isLoading={status === 'loading'}>
        Update Password
      </Button>
    </Stack>
  )
}

export default ResetPasswordForm
